/**
 * Platform documentation page.
 *
 * Comprehensive admin and user guide for the Velosynt security platform.
 * Sections are navigable via the left-hand table of contents and by anchor links
 * (e.g. /docs#agents) so other pages can deep-link into specific sections.
 */

import { useEffect, useRef, useState } from 'react';
import {useLocation,useNavigate} from 'react-router-dom';
import {coreApiMode} from '@/config/deployment';
import CloudflareDocsContent,{cloudflareSections} from './CloudflareDocsContent';
import { useAuth } from '../hooks/useAuth';
import {
  BookOpen,
  LayoutDashboard,
  Server,
  Radar,
  ShieldAlert,
  FileText,
  Bot,
  Building2,
  Settings,
  User,
  ChevronRight,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Section {
  id: string;
  label: string;
  icon: React.ReactNode;
  subsections?: { id: string; label: string }[];
}

// ---------------------------------------------------------------------------
// Table-of-contents structure
// ---------------------------------------------------------------------------

const legacySections: Section[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: <BookOpen className="h-4 w-4" />,
    subsections: [
      { id: 'overview-intro', label: 'Introduction' },
      { id: 'overview-roles', label: 'User Roles' },
    ],
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: <LayoutDashboard className="h-4 w-4" />,
  },
  {
    id: 'assets',
    label: 'Assets',
    icon: <Server className="h-4 w-4" />,
    subsections: [
      { id: 'assets-add', label: 'Adding Assets' },
      { id: 'assets-ports', label: 'Ports' },
      { id: 'assets-credentials', label: 'Credentials' },
    ],
  },
  {
    id: 'scans',
    label: 'Scans',
    icon: <Radar className="h-4 w-4" />,
    subsections: [
      { id: 'scans-types', label: 'Scan Types' },
      { id: 'scans-create', label: 'Creating a Scan' },
      { id: 'scans-status', label: 'Scan Lifecycle' },
      { id: 'scans-schedules', label: 'Scheduled Scans' },
      { id: 'scans-engines', label: 'Scanner Engines' },
    ],
  },
  {
    id: 'findings',
    label: 'Findings',
    icon: <ShieldAlert className="h-4 w-4" />,
    subsections: [
      { id: 'findings-severity', label: 'Severity Levels' },
      { id: 'findings-lifecycle', label: 'Finding Lifecycle' },
      { id: 'findings-cve', label: 'CVE Enrichment' },
      { id: 'findings-rescan', label: 'Targeted Rescan' },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: <FileText className="h-4 w-4" />,
  },
  {
    id: 'agents',
    label: 'Remote Agents',
    icon: <Bot className="h-4 w-4" />,
    subsections: [
      { id: 'agents-what', label: 'What Are Agents?' },
      { id: 'agents-setup', label: 'Agent Setup' },
      { id: 'agents-connection', label: 'Connection Types' },
      { id: 'agents-scanning', label: 'Scanning with Agents' },
    ],
  },
  {
    id: 'org-settings',
    label: 'Org Settings',
    icon: <Building2 className="h-4 w-4" />,
    subsections: [
      { id: 'org-users', label: 'User Management' },
      { id: 'org-invite', label: 'Inviting Users' },
    ],
  },
  {
    id: 'admin',
    label: 'Platform Admin',
    icon: <Settings className="h-4 w-4" />,
    subsections: [
      { id: 'admin-tenants', label: 'Tenant Management' },
      { id: 'admin-lifecycle', label: 'Tenant Lifecycle' },
      { id: 'admin-slug', label: 'Slug & Schema Name' },
      { id: 'admin-tiers', label: 'Subscription Tiers' },
    ],
  },
  {
    id: 'profile',
    label: 'Profile & Account',
    icon: <User className="h-4 w-4" />,
  },
];
const sections:Section[]=coreApiMode?cloudflareSections:legacySections;

// ---------------------------------------------------------------------------
// Small reusable components
// ---------------------------------------------------------------------------

function SectionHeading({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2 id={id} className="scroll-mt-6 text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">
      {children}
    </h2>
  );
}

function SubHeading({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h3 id={id} className="scroll-mt-6 text-lg font-semibold text-gray-800 mt-6 mb-2">
      {children}
    </h3>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 my-3">
      <strong>Note:</strong> {children}
    </div>
  );
}

function Warning({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 my-3">
      <strong>Important:</strong> {children}
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto my-4">
      <table className="min-w-full divide-y divide-gray-200 text-sm border border-gray-200 rounded-lg overflow-hidden">
        <thead className="bg-gray-50">
          <tr>
            {headers.map((h) => (
              <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-700">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CodeSnip({ children }: { children: string }) {
  return (
    <code className="rounded bg-gray-100 border border-gray-200 px-1.5 py-0.5 text-xs font-mono text-gray-800">
      {children}
    </code>
  );
}

function Block({ children }: { children: string }) {
  return (
    <pre className="rounded bg-gray-900 text-green-400 text-xs p-4 overflow-x-auto font-mono whitespace-pre my-3">
      {children}
    </pre>
  );
}

// ---------------------------------------------------------------------------
// Documentation content
// ---------------------------------------------------------------------------

function DocsContent({ isPlatformAdmin }: { isPlatformAdmin: boolean }) {
  return (
    <div className="prose-gray max-w-none space-y-10 text-sm text-gray-700 leading-relaxed">

      {/* ── Overview ──────────────────────────────────────────────────── */}
      <section>
        <SectionHeading id="overview">Overview</SectionHeading>

        <SubHeading id="overview-intro">Introduction</SubHeading>
        <p>
          Vantage by Velosynt is a multi-tenant vulnerability management platform that helps security teams
          discover, assess, and remediate weaknesses across their infrastructure. It combines
          cloud-based scanning with the option to deploy lightweight on-premises agents for
          environments that are not reachable from the internet.
        </p>
        <p className="mt-2">
          The platform orchestrates multiple open-source scanner engines (Nmap, Nuclei, Nikto,
          SSLyze, SSH Audit, WinRM Audit, and custom checks), collects findings, enriches them with
          CVSS scores and CVE data from the NIST National Vulnerability Database (NVD), and presents
          them in a unified dashboard with actionable remediation guidance.
        </p>

        <SubHeading id="overview-roles">User Roles</SubHeading>
        <p>Every user is assigned one of three roles that controls what they can see and do:</p>
        <Table
          headers={['Role', 'Description', 'Key Permissions']}
          rows={[
            ['platform_admin', 'Velosynt staff / super-admin', 'Manage all tenants, tiers, and platform settings'],
            ['customer_admin', 'Organisation administrator', 'Manage users, scans, assets, and org settings within their tenant'],
            ['analyst', 'Security analyst', 'View and triage findings; read-only access to scans and assets'],
          ]}
        />
        <Note>
          Role-based navigation automatically hides menu items that the current user cannot access.
          Platform admins see the <strong>Admin</strong> panel; customer admins see{' '}
          <strong>Org Settings</strong>; analysts see only operational pages.
        </Note>
      </section>

      {/* ── Dashboard ─────────────────────────────────────────────────── */}
      <section>
        <SectionHeading id="dashboard">Dashboard</SectionHeading>
        <p>
          The Dashboard is the first page you see after logging in. It gives an at-a-glance view of
          your organisation's current security posture with metric cards showing:
        </p>
        <ul className="list-disc pl-6 space-y-1 mt-2">
          <li><strong>Total Assets</strong> — number of assets registered in your tenant</li>
          <li><strong>Active Scans</strong> — scans currently running or pending</li>
          <li><strong>Open Findings</strong> — total unresolved vulnerabilities</li>
          <li><strong>Critical Findings</strong> — high-priority issues requiring immediate attention</li>
        </ul>
        <p className="mt-3">
          Below the metrics, a summary of recent findings broken down by severity (Critical, High,
          Medium, Low, Info) helps you understand risk concentration. Recent scan activity is also
          displayed so you can quickly navigate to results.
        </p>
      </section>

      {/* ── Assets ────────────────────────────────────────────────────── */}
      <section>
        <SectionHeading id="assets">Assets</SectionHeading>
        <p>
          Assets are the targets you want to scan — servers, workstations, web applications, and
          network devices. You must register an asset before it can be included in a scan.
        </p>

        <SubHeading id="assets-add">Adding Assets</SubHeading>
        <p>
          Navigate to <strong>Assets → New Asset</strong>. Provide:
        </p>
        <ul className="list-disc pl-6 space-y-1 mt-1">
          <li><strong>Name / Label</strong> — a human-readable identifier (e.g. "Web Server 01")</li>
          <li>
            <strong>Value</strong> — the address to scan. Accepts:
            <ul className="list-disc pl-6 mt-1 space-y-1">
              <li>Hostnames: <CodeSnip>db.internal.example.com</CodeSnip></li>
              <li>IPv4 addresses: <CodeSnip>10.0.1.15</CodeSnip></li>
              <li>URLs: <CodeSnip>https://app.example.com</CodeSnip> (the hostname is extracted automatically)</li>
            </ul>
          </li>
          <li><strong>Asset Type</strong> — Host, Web Application, Network Device, etc.</li>
        </ul>

        <SubHeading id="assets-ports">Ports</SubHeading>
        <p>
          By default scanners probe common ports. You can add explicit port numbers to an asset
          (e.g. <CodeSnip>8443</CodeSnip>, <CodeSnip>3306</CodeSnip>) so that Nmap and other engines
          focus on those specific ports rather than running a broad sweep. Port entries consist of a
          port number and optionally a protocol (TCP/UDP).
        </p>

        <SubHeading id="assets-credentials">Credentials</SubHeading>
        <p>
          Some scanner engines can use credentials to perform authenticated checks (e.g. SSH banner
          grabbing, WinRM audits). Credentials are stored encrypted at rest and are never returned
          in plain text after being saved.
        </p>
        <p className="mt-2">Supported credential types:</p>
        <Table
          headers={['Type', 'Fields', 'Used By']}
          rows={[
            ['SSH Password', 'Username, Password', 'SSH Audit scanner'],
            ['SSH Private Key', 'Username, PEM private key', 'SSH Audit scanner'],
            ['WinRM Password', 'Username, Password', 'WinRM Audit scanner'],
            ['SNMP v1/v2c', 'Community string', 'SNMP scanner'],
            ['SNMPv3', 'Security name (username), Auth protocol, Auth password, Privacy protocol, Privacy password, Security level', 'SNMP scanner'],
          ]}
        />
        <p className="mt-2">
          SNMPv3 supports three security levels:
        </p>
        <ul className="list-disc pl-6 space-y-1 mt-1">
          <li><strong>noAuthNoPriv</strong> — no authentication or encryption (not recommended)</li>
          <li><strong>authNoPriv</strong> — authentication only (MD5, SHA, SHA-256, or SHA-512)</li>
          <li><strong>authPriv</strong> — authentication and encryption (recommended; adds AES or DES privacy)</li>
        </ul>
        <Warning>
          Credentials are linked to an asset, not to a scan. Any scan that targets an asset will
          automatically receive that asset's credentials.
        </Warning>
      </section>

      {/* ── Scans ─────────────────────────────────────────────────────── */}
      <section>
        <SectionHeading id="scans">Scans</SectionHeading>
        <p>
          A scan is a job that runs one or more scanner engines against a set of assets and collects
          findings. Scans can be triggered on-demand or on a recurring schedule.
        </p>

        <SubHeading id="scans-types">Scan Types</SubHeading>
        <Table
          headers={['Scan Type', 'Engines Used', 'Best For']}
          rows={[
            ['Full', 'Nmap, Nuclei, Custom Checks, Nikto, SSLyze, SSH Audit, WinRM Audit', 'Complete security assessment of all services'],
            ['Discovery', 'Nmap', 'Port mapping and service identification'],
            ['Vulnerability', 'Nuclei, Custom Checks', 'Known CVE and misconfiguration detection'],
            ['Web', 'Nikto, Custom Checks', 'Web application vulnerability scanning'],
            ['SSL', 'SSLyze', 'TLS/SSL certificate and cipher analysis'],
            ['Custom', 'Caller-specified', 'Advanced users who want fine-grained engine selection'],
          ]}
        />

        <SubHeading id="scans-create">Creating a Scan</SubHeading>
        <ol className="list-decimal pl-6 space-y-2 mt-1">
          <li>Navigate to <strong>Scans → New Scan</strong>.</li>
          <li>Enter a descriptive <strong>Scan Name</strong>.</li>
          <li>Choose a <strong>Scan Type</strong> from the table above.</li>
          <li>
            Select one or more <strong>Target Assets</strong>. Only assets already registered in
            your organisation appear in the list.
          </li>
          <li>
            (Optional) If your organisation has remote agents, select an <strong>Agent</strong> to
            run the scan on-premises instead of from the cloud.
          </li>
          <li>Click <strong>Start Scan</strong>. The scan enters <em>pending</em> status and is
            dispatched to the scanning worker within seconds.</li>
        </ol>
        <Note>
          Customer admins and platform admins can create scans. Analysts have read-only access.
        </Note>

        <SubHeading id="scans-status">Scan Lifecycle</SubHeading>
        <Table
          headers={['Status', 'Description']}
          rows={[
            ['Pending', 'Scan created and queued, waiting for a worker'],
            ['Running', 'Actively scanning targets; progress percentage shown'],
            ['Completed', 'All engines finished; findings are available'],
            ['Failed', 'An unrecoverable error occurred; error message is displayed'],
            ['Cancelled', 'Manually cancelled by an admin before completion'],
          ]}
        />
        <p className="mt-2">
          To cancel a running scan, open the scan detail and click <strong>Cancel</strong>. Only
          scans in <em>pending</em> or <em>running</em> state can be cancelled.
        </p>

        <SubHeading id="scans-schedules">Scheduled Scans</SubHeading>
        <p>
          Recurring scans are defined using cron expressions and are triggered automatically by the
          platform scheduler. Navigate to <strong>Scans → Schedules</strong> to manage them.
        </p>
        <p className="mt-2">Each schedule requires:</p>
        <ul className="list-disc pl-6 space-y-1 mt-1">
          <li><strong>Name</strong> — human-readable label</li>
          <li><strong>Scan Type</strong> — same options as on-demand scans</li>
          <li><strong>Target Assets</strong> — must include at least one asset</li>
          <li><strong>Cron Expression</strong> — standard 5-field cron (minute hour day month weekday)</li>
        </ul>
        <p className="mt-2">Common cron expressions:</p>
        <Table
          headers={['Expression', 'Meaning']}
          rows={[
            ['0 2 * * *', 'Every day at 02:00 UTC'],
            ['0 0 * * 1', 'Every Monday at midnight UTC'],
            ['0 */6 * * *', 'Every 6 hours'],
            ['30 1 1 * *', 'First day of each month at 01:30 UTC'],
          ]}
        />
        <Note>
          Scheduled scans always run in the cloud unless you modify the schedule to target an agent.
          All times are in UTC.
        </Note>

        <SubHeading id="scans-engines">Scanner Engines</SubHeading>
        <Table
          headers={['Engine', 'Purpose']}
          rows={[
            ['nmap', 'Port scanning, service version detection, OS fingerprinting, PTR hostname resolution'],
            ['nuclei', 'Template-based vulnerability detection (CVEs, misconfigurations, exposures)'],
            ['nikto', 'Web server vulnerability and configuration scanning'],
            ['sslyze', 'TLS/SSL certificate validity, weak ciphers, and protocol version checks'],
            ['custom_checks', 'Built-in Vantage checks for common misconfigurations (default creds, exposed admin interfaces, etc.)'],
            ['ssh_audit', 'SSH server configuration audit (algorithms, key exchange, MACs)'],
            ['winrm_audit', 'Windows Remote Management (WinRM) configuration and authentication checks'],
          ]}
        />
      </section>

      {/* ── Findings ──────────────────────────────────────────────────── */}
      <section>
        <SectionHeading id="findings">Findings</SectionHeading>
        <p>
          A finding is a vulnerability, misconfiguration, or informational observation detected by a
          scanner engine. Each finding is associated with a specific asset and scan.
        </p>

        <SubHeading id="findings-severity">Severity Levels</SubHeading>
        <Table
          headers={['Severity', 'CVSS Range', 'Description']}
          rows={[
            ['Critical', '9.0 – 10.0', 'Immediate exploitation risk; fix within 24 hours'],
            ['High', '7.0 – 8.9', 'Serious vulnerability; fix within 7 days'],
            ['Medium', '4.0 – 6.9', 'Moderate risk; fix within 30 days'],
            ['Low', '0.1 – 3.9', 'Minor issue; fix in next maintenance window'],
            ['Info', 'N/A', 'Informational observation; no direct risk'],
          ]}
        />

        <SubHeading id="findings-lifecycle">Finding Lifecycle</SubHeading>
        <Table
          headers={['Status', 'Description']}
          rows={[
            ['Open', 'Newly detected; not yet reviewed'],
            ['Confirmed', 'Verified by a targeted rescan or analyst review'],
            ['In Progress', 'Remediation work has started'],
            ['Resolved', 'Vulnerability no longer detected or manually marked resolved'],
            ['Accepted Risk', 'Risk accepted by an authorised stakeholder; will not be re-alerted'],
            ['False Positive', 'Finding determined to be a false alarm'],
          ]}
        />
        <p className="mt-2">
          Analysts and admins can update finding status from the Findings list or from the finding
          detail panel. Bulk status updates are supported via the checkbox selection.
        </p>

        <SubHeading id="findings-cve">CVE Enrichment</SubHeading>
        <p>
          When a scanner engine reports a CVE identifier (e.g. <CodeSnip>CVE-2021-44228</CodeSnip>),
          the platform automatically queries the NIST National Vulnerability Database (NVD) API to
          retrieve:
        </p>
        <ul className="list-disc pl-6 space-y-1 mt-1">
          <li>CVSS v3.x base score and vector string</li>
          <li>Official CVE description</li>
          <li>CWE classification</li>
        </ul>
        <p className="mt-2">
          This enrichment happens asynchronously after the scan completes and is visible in the
          finding detail panel as <strong>CVSS Score</strong> and <strong>CVE ID</strong>.
        </p>
        <Note>
          CVE enrichment requires a valid NVD API key configured by the platform administrator. An
          unauthenticated NVD key is rate-limited to 5 requests per 30 seconds, which may slow
          enrichment for scans with many CVE findings.
        </Note>

        <SubHeading id="findings-rescan">Targeted Rescan</SubHeading>
        <p>
          A targeted rescan re-runs only the specific engine that originally detected a finding
          against the same asset. This confirms whether the vulnerability has been patched without
          running a full scan.
        </p>
        <p className="mt-2">To trigger a targeted rescan:</p>
        <ol className="list-decimal pl-6 space-y-1 mt-1">
          <li>Open a finding's detail panel.</li>
          <li>Click <strong>Verify / Rescan</strong>.</li>
          <li>
            The finding status updates automatically:
            <ul className="list-disc pl-6 mt-1">
              <li><em>Confirmed</em> — vulnerability still present after rescan</li>
              <li><em>Resolved</em> — vulnerability no longer detected</li>
            </ul>
          </li>
        </ol>
      </section>

      {/* ── Reports ───────────────────────────────────────────────────── */}
      <section>
        <SectionHeading id="reports">Reports</SectionHeading>
        <p>
          The Reports section provides exportable summaries of your security posture for stakeholders
          and compliance documentation.
        </p>
        <p className="mt-2">Available report formats and types:</p>
        <ul className="list-disc pl-6 space-y-1 mt-1">
          <li><strong>Executive Summary</strong> — high-level risk overview suitable for non-technical leadership</li>
          <li><strong>Vulnerability Report</strong> — full list of findings with severity, asset, and remediation details</li>
          <li><strong>Scan History</strong> — chronological list of all scan runs with outcomes</li>
          <li><strong>Asset Inventory</strong> — registered assets with associated risk counts</li>
        </ul>
        <p className="mt-2">
          Reports can be generated on-demand from the Reports page. PDF and CSV export formats are
          available depending on the subscription tier.
        </p>
      </section>

      {/* ── Agents ────────────────────────────────────────────────────── */}
      <section>
        <SectionHeading id="agents">Remote Agents</SectionHeading>

        <SubHeading id="agents-what">What Are Agents?</SubHeading>
        <p>
          Remote agents are lightweight Docker containers that run inside your network and execute
          scans against devices that are not reachable from the internet. The agent makes
          outbound-only HTTPS connections to Vantage by Velosynt — no inbound firewall rules are
          required.
        </p>
        <p className="mt-2">
          Use agents when your targets are on an isolated network segment (office LAN, data centre
          VLAN, air-gapped environment) that cannot be reached by Vantage's cloud scanners.
        </p>
        <Note>
          Agent availability depends on your subscription tier. Starter and higher tiers support
          remote agents. The Free tier uses cloud-only scanning.
        </Note>

        <SubHeading id="agents-setup">Agent Setup</SubHeading>
        <p>Follow these steps to deploy a remote agent:</p>

        <p className="font-semibold mt-4">Step 1 — Prerequisites</p>
        <ul className="list-disc pl-6 space-y-1 mt-1">
          <li>A Linux server (physical or VM) with Docker and Docker Compose installed</li>
          <li>Outbound HTTPS access (port 443) to <CodeSnip>staging-vantage.velosynt.com</CodeSnip> or your platform URL</li>
          <li>Sufficient CPU and RAM to run scanner tools (minimum 2 vCPU, 2 GB RAM recommended)</li>
        </ul>

        <p className="font-semibold mt-4">Step 2 — Pull the agent image</p>
        <Block>docker pull ghcr.io/velosynt/agent:latest</Block>

        <p className="font-semibold mt-4">Step 3 — Register the agent in Vantage</p>
        <ol className="list-decimal pl-6 space-y-1 mt-1">
          <li>Navigate to <strong>Scans → Agents</strong> (or the Agents page from your sidebar).</li>
          <li>Click <strong>New Agent</strong>.</li>
          <li>Enter a descriptive name (e.g. "Office Network Agent").</li>
          <li>Choose a connection type (see <a href="#agents-connection" className="text-blue-600 hover:underline">Connection Types</a> below).</li>
          <li>Copy the API key shown in the banner — <strong>it will not be shown again</strong>.</li>
        </ol>

        <p className="font-semibold mt-4">Step 4 — Create docker-compose.yml</p>
        <Block>{`services:
  velosynt-agent:
    image: ghcr.io/velosynt/agent:latest
    restart: unless-stopped
    network_mode: host          # gives agent direct LAN access
    environment:
      PLATFORM_URL: "https://staging-vantage.velosynt.com"
      AGENT_API_KEY: "vsa_..."  # paste key from Step 3
      AGENT_CONNECTION_TYPE: "long_poll"
    volumes:
      - nuclei-templates:/root/nuclei-templates
volumes:
  nuclei-templates:`}</Block>

        <p className="font-semibold mt-4">Step 5 — Start the agent</p>
        <Block>{`docker compose up -d

# Verify it's running and connected:
docker compose logs -f`}</Block>

        <p className="mt-2">
          Within 30 seconds the agent status in Vantage will change to{' '}
          <span className="inline-flex items-center gap-1 font-medium text-green-700">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-500" />
            Online
          </span>.
        </p>

        <SubHeading id="agents-connection">Connection Types</SubHeading>
        <Table
          headers={['Type', 'How It Works', 'Best For']}
          rows={[
            ['long_poll', 'Agent repeatedly polls the platform for pending jobs over HTTPS', 'Environments with strict egress filtering; most compatible'],
            ['websocket', 'Agent maintains a persistent WebSocket connection for instant job dispatch', 'Low-latency environments where near-instant scan start is important'],
          ]}
        />
        <p className="mt-2">
          Long Poll is recommended for most deployments. The agent checks for new jobs every few
          seconds, so the delay is minimal in practice.
        </p>
        <Note>
          Changing the connection type on an existing agent takes effect only after you restart the
          agent container on your server (<CodeSnip>docker compose restart</CodeSnip>).
        </Note>

        <SubHeading id="agents-scanning">Scanning with Agents</SubHeading>
        <p>
          When creating an on-demand scan, select the agent from the <strong>Agent</strong> dropdown
          in the new scan form. The scan job is dispatched to that agent instead of being queued for
          the cloud workers.
        </p>
        <p className="mt-2">
          Scheduled scans currently use cloud workers. Agent-dispatched scheduling is on the roadmap.
        </p>
        <Warning>
          If the selected agent is offline when a scan is created, the scan job is stored and will
          be delivered to the agent as soon as it reconnects. The scan will remain in{' '}
          <em>pending</em> status until the agent picks it up.
        </Warning>
      </section>

      {/* ── Org Settings ──────────────────────────────────────────────── */}
      <section>
        <SectionHeading id="org-settings">Org Settings</SectionHeading>
        <p>
          Organisation settings are accessible to customer admins and cover user management,
          invitations, and organisation profile. Navigate to <strong>Org Settings</strong> from the
          sidebar.
        </p>

        <SubHeading id="org-users">User Management</SubHeading>
        <p>The Users tab lists all users in your organisation with their role, status, and last activity.</p>
        <p className="mt-2">Available actions for each user:</p>
        <ul className="list-disc pl-6 space-y-1 mt-1">
          <li><strong>Edit</strong> — change name, email, or role</li>
          <li><strong>Deactivate</strong> — soft-delete; user can no longer log in but data is retained</li>
          <li><strong>Reactivate</strong> — restore access for a previously deactivated user</li>
          <li><strong>Send Password Reset</strong> — dispatch a password reset email to the user</li>
          <li><strong>Set Password</strong> — directly set a user's password without sending an email (useful for onboarding)</li>
          <li><strong>Remove</strong> — permanently hard-delete the user (cannot be undone)</li>
        </ul>

        <SubHeading id="org-invite">Inviting Users</SubHeading>
        <p>
          To add a new team member without creating their password manually, send an invitation:
        </p>
        <ol className="list-decimal pl-6 space-y-1 mt-1">
          <li>Navigate to <strong>Org Settings → Invitations</strong>.</li>
          <li>Click <strong>Send Invite</strong>.</li>
          <li>Enter the new user's email address, full name, and role.</li>
          <li>Click <strong>Send</strong> — the user receives an email with a one-time registration link.</li>
        </ol>
        <p className="mt-2">
          Invitation links expire after the configured period (default 48 hours). You can resend or
          revoke pending invitations from the Invitations tab.
        </p>
        <Note>
          Invitations and user creation both count against your organisation's user quota set by the
          subscription tier.
        </Note>
      </section>

      {/* ── Platform Admin ────────────────────────────────────────────── */}
      {isPlatformAdmin && (
        <section>
          <SectionHeading id="admin">Platform Admin</SectionHeading>
          <p>
            The Admin panel is only visible to users with the <CodeSnip>platform_admin</CodeSnip>{' '}
            role. It provides full control over all tenants and platform configuration.
          </p>

          <SubHeading id="admin-tenants">Tenant Management</SubHeading>
          <p>
            Each customer organisation is a <em>tenant</em> with its own isolated database schema.
            From the <strong>Admin → Tenants</strong> tab you can:
          </p>
          <ul className="list-disc pl-6 space-y-1 mt-1">
            <li>Create new tenants and provision their initial admin user</li>
            <li>View tenant details — name, slug, schema name, tier, user count, and status</li>
            <li>Change a tenant's subscription tier</li>
            <li>Deactivate, reactivate, or permanently delete a tenant</li>
          </ul>
          <p className="mt-3">
            Click the <strong>Edit</strong> button on any tenant row (or click the row itself) to
            open the tenant detail panel. It has three tabs:
          </p>
          <Table
            headers={['Tab', 'Purpose']}
            rows={[
              ['Settings', 'Edit name and tier; Deactivate, Reactivate, or Delete the organisation'],
              ['Usage', 'Resource usage bars — users, assets, credentials, and scans against tier limits'],
              ['Users', 'List all users in the tenant; change roles, reset passwords, deactivate, or delete individual users'],
            ]}
          />

          <SubHeading id="admin-lifecycle">Tenant Lifecycle</SubHeading>
          <p>
            The Settings tab provides three lifecycle actions, each with a distinct effect:
          </p>
          <Table
            headers={['Action', 'Effect', 'Reversible?']}
            rows={[
              ['Deactivate', 'Marks the tenant and all its users as inactive. Users cannot log in. All data is preserved.', 'Yes — Reactivate restores access instantly'],
              ['Reactivate', 'Restores an inactive tenant and re-enables all user accounts.', 'Yes — can be deactivated again'],
              ['Delete', 'Permanently removes the tenant row, all user accounts, and the entire database schema (all assets, scans, findings, reports, and credentials).', 'No — irreversible'],
            ]}
          />
          <Note>
            <strong>Deactivate</strong> is the right choice when a customer's subscription lapses or
            they request a temporary suspension. Their data is fully preserved and access can be
            restored the moment they renew. Use <strong>Delete</strong> only when a customer has
            explicitly requested full data removal or after the data retention period has passed.
          </Note>
          <Warning>
            The Delete action requires typing the exact organisation name to confirm. Once confirmed,
            the database schema is dropped with <CodeSnip>CASCADE</CodeSnip> — all data is gone and
            cannot be recovered. There is no soft-delete or recycle bin.
          </Warning>

          <SubHeading id="admin-slug">Slug &amp; Schema Name</SubHeading>
          <p>
            When you create a tenant you must provide a <strong>slug</strong> — a short, lowercase
            identifier that permanently determines the tenant's PostgreSQL database schema name.
          </p>
          <p className="mt-2">
            The mapping is straightforward: hyphens are converted to underscores and prefixed with{' '}
            <CodeSnip>tenant_</CodeSnip>:
          </p>
          <Table
            headers={['Slug', 'Database Schema']}
            rows={[
              ['acme-corp', 'tenant_acme_corp'],
              ['bisk', 'tenant_bisk'],
              ['client-123', 'tenant_client_123'],
            ]}
          />
          <p className="mt-2">
            All of that tenant's data — assets, scans, findings, reports, and credentials — lives
            exclusively inside that schema. Schemas are isolated from each other; a query in one
            tenant's session can never access another tenant's data.
          </p>
          <p className="mt-2">Slug rules:</p>
          <ul className="list-disc pl-6 space-y-1 mt-1">
            <li>Lowercase letters and numbers only</li>
            <li>Hyphens allowed between segments (e.g. <CodeSnip>acme-corp</CodeSnip>)</li>
            <li>3–63 characters</li>
            <li>Must be unique across all tenants</li>
          </ul>
          <Warning>
            The slug is <strong>permanent and cannot be changed</strong> after the tenant is created.
            Changing it would break the link between the <CodeSnip>tenants</CodeSnip> table record
            and the underlying database schema. Choose a stable, recognisable identifier — typically
            a short company or project name.
          </Warning>
          <p>
            The slug and its derived schema name are visible in the tenant detail panel in the Admin
            section (read-only).
          </p>

          <SubHeading id="admin-tiers">Subscription Tiers</SubHeading>
          <p>
            Tiers define feature limits and quotas for each tenant. The platform ships with the
            following default tiers:
          </p>
          <Table
            headers={['Tier', 'Users', 'Assets', 'Scans/Month', 'Agents', 'Notes']}
            rows={[
              ['Free', '3', '10', '10', '0', 'Cloud scanning only'],
              ['Starter', '10', '50', '50', '2', 'Long poll agents'],
              ['Professional', '25', '200', '200', '5', 'WebSocket agents, scheduled scans'],
              ['Enterprise', 'Unlimited', 'Unlimited', 'Unlimited', 'Unlimited', 'All features, priority support'],
            ]}
          />
          <p className="mt-2">
            Tier configurations (user limits, scan quotas, allowed connection types) can be adjusted
            from the Admin → Tiers page.
          </p>
        </section>
      )}

      {/* ── Profile ───────────────────────────────────────────────────── */}
      <section>
        <SectionHeading id="profile">Profile &amp; Account</SectionHeading>
        <p>
          Click your name or avatar in the top bar and select <strong>Profile</strong> to manage
          your personal account settings.
        </p>
        <p className="mt-2">From the Profile page you can:</p>
        <ul className="list-disc pl-6 space-y-1 mt-1">
          <li>Update your full name and email address</li>
          <li>Change your password (requires current password for verification)</li>
          <li>Upload a profile avatar</li>
        </ul>
        <p className="mt-2">
          You can also click your avatar in the <strong>bottom of the sidebar</strong> to navigate
          directly to your profile page.
        </p>
        <Note>
          Changing your email address will update your login credential immediately. Make sure you
          have access to the new email address before saving.
        </Note>
      </section>

    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export default function DocsPage() {
  const { isPlatformAdmin } = useAuth();
  const location=useLocation(),navigate=useNavigate();
  const [activeSection, setActiveSection] = useState('overview');
  const contentRef = useRef<HTMLDivElement>(null);
  const visibleSections = sections.filter((s) => s.id !== 'admin' || isPlatformAdmin);

  // Scroll to the hash anchor on initial load and when hash changes
  useEffect(() => {
    const hash = location.hash.slice(1);
    if (hash) {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const section=sections.find(s=>s.id===hash||s.subsections?.some(sub=>sub.id===hash));
        if(section)setActiveSection(section.id);
      }
    }
  }, [location.hash]);

  // Highlight the active TOC entry based on scroll position
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            // Match the top-level section id (first segment before '-')
            const topLevel = sections.find(
              (s) => s.id === id || s.subsections?.some((sub) => sub.id === id),
            );
            if (topLevel) setActiveSection(topLevel.id);
          }
        }
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 },
    );

    const allIds = sections.flatMap((s) => [
      s.id,
      ...(s.subsections?.map((sub) => sub.id) ?? []),
    ]);
    allIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  function scrollTo(id: string) {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      navigate({hash:`#${id}`},{replace:true});
    }
  }

  return (
    <div className="flex flex-col gap-8 min-h-full lg:flex-row">
      {/* ── Table of Contents ── */}
      <aside className="w-full shrink-0 lg:w-56">
        <nav aria-label="Documentation contents" className="space-y-0.5 lg:sticky lg:top-0 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="h-5 w-5 text-indigo-600" />
            <h2 className="text-base font-bold text-gray-900">Documentation</h2>
          </div>

          {visibleSections.map((section) => (
            <div key={section.id}>
              <button
                aria-current={activeSection===section.id?'true':undefined}
                onClick={() => {
                  scrollTo(section.id);
                  setActiveSection(section.id);
                }}
                className={`w-full flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm text-left transition-colors ${
                  activeSection === section.id
                    ? 'bg-indigo-50 text-indigo-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <span className="shrink-0 text-gray-400">{section.icon}</span>
                {section.label}
              </button>

              {section.subsections && activeSection === section.id && (
                <div className="ml-6 mt-0.5 space-y-0.5">
                  {section.subsections.map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => scrollTo(sub.id)}
                      className="w-full flex items-center gap-1 rounded px-2 py-1 text-xs text-left text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                    >
                      <ChevronRight className="h-3 w-3 shrink-0" />
                      {sub.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
      </aside>

      {/* ── Content ── */}
      <div ref={contentRef} className="flex-1 min-w-0">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Vantage by Velosynt Documentation</h1>
          <p className="mt-1 text-sm text-gray-500">
            Administrator and user guide — last updated {coreApiMode?'October':'March'} 2026
          </p>
        </div>
        {coreApiMode?<CloudflareDocsContent isPlatformAdmin={isPlatformAdmin}/>:<DocsContent isPlatformAdmin={isPlatformAdmin} />}
      </div>
    </div>
  );
}
